import { RuleModel } from '../../../../types';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  getAllResourceCalendars,
  getDocById,
  getSharedDrivesByIdsQuery,
  UserKeys,
} from '../../../api/users-api';
import RulesSummaryWhenSection from './rules-summary-when-section';
import RulesSummaryIfSection from './rules-summary-if-section';
import RuleSummaryThenSection from './rules-summary-then-section';
import { TriggerSource } from './rules-engine-form-page';
import {
  generateRuleSummary,
  getConditionSummaryItems,
} from './rule-summary-panel-utils';
import { actionOptions } from './action-options';
import { triggerEventOptions } from './trigger-event-options';
import { useMemo } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface RulesDetailsSummaryProps {
  selectedRule: RuleModel;
}

export default function RulesDetailsSummary({
  selectedRule,
}: RulesDetailsSummaryProps) {
  const theme = useTheme();
  const sharedDriveIds = selectedRule.triggerInfo.sharedDriveIds || [];
  const { data: sharedDrives, isLoading: isLoadingSharedDrives } = useQuery({
    ...getSharedDrivesByIdsQuery(sharedDriveIds),
    enabled: sharedDriveIds.length > 0,
  });

  const { data: folderData, pending: isLoadingFolderData } = useQueries({
    queries: (selectedRule.triggerInfo.driveFolderTriggerSources || [])
      .filter((s) => s.driveFolderId && s.driveFolderOwner)
      .map((source) => ({
        queryKey: [
          UserKeys.DriveFolder,
          source.driveFolderId,
          source.driveFolderOwner,
        ],
        queryFn: () =>
          getDocById(source.driveFolderOwner, source.driveFolderId),
        staleTime: 1000 * 60 * 5,
      })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const { data: resourceCalendars, isLoading: isLoadingCalendars } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
  });

  const triggerSources = useMemo<TriggerSource[]>(() => {
    return [
      ...(selectedRule.triggerInfo.triggerUserSources?.map((value) => ({
        type: 'User',
        value,
      })) || []),

      ...(selectedRule.triggerInfo.triggerGroupSources?.map((value) => ({
        type: 'Group',
        value,
      })) || []),

      ...(selectedRule.triggerInfo.triggerOrgSources?.map((value) => ({
        type: 'Orgs',
        value,
      })) || []),

      ...(selectedRule.triggerInfo.triggerDomainSources?.map((value) => ({
        type: 'Domain',
        value,
      })) || []),

      ...(selectedRule.triggerInfo.sharedDriveIds?.map((id) => {
        const drive = sharedDrives?.find((d) => d.id === id);

        return {
          type: 'Shared Drive',
          value: drive ? drive.name : id,
          selectedSharedDriveId: id,
        };
      }) || []),

      ...(selectedRule.triggerInfo.driveRegExs?.map((value) => ({
        type: 'RegEx',
        value,
      })) || []),

      ...(selectedRule.triggerInfo.driveFolderTriggerSources?.map((source) => {
        const folder = folderData?.find((f) => f?.id === source.driveFolderId);

        return {
          type: 'User Drive',
          value: folder?.title
            ? `${source.driveFolderOwner}: ${folder.title}`
            : `${source.driveFolderOwner}: ${source.driveFolderId}`,
          driveFolderOwner: source.driveFolderOwner,
          driveFolderId: source.driveFolderId,
          driveFolderName: folder?.title,
        };
      }) || []),

      ...(selectedRule.triggerInfo.triggerCalendarSources?.map((email) => {
        const calendar = resourceCalendars?.find(
          (calendar) => calendar.resourceEmail === email
        );

        return {
          type: 'Resource Calendar',
          value: calendar?.resourceName || email,
        };
      }) || []),
    ];
  }, [selectedRule.triggerInfo, sharedDrives, folderData, resourceCalendars]);

  const triggerEventOption = triggerEventOptions.find(
    (opt) =>
      opt.value === selectedRule.triggerInfo.triggerEvent &&
      opt.type === selectedRule.triggerInfo.triggerGroup
  );
  const triggerActionLabel = triggerEventOption?.displayValue ?? '-';
  const actionTodo = selectedRule.actionInfo?.actionTodo || ['-'];
  const actionItems: string[] =
    actionTodo && actionTodo.length > 0
      ? actionTodo.map(
          (action) =>
            actionOptions.find((option) => option.value === action)
              ?.displayValue ?? action
        )
      : [];

  const conditionSummaryItems = getConditionSummaryItems(
    selectedRule.conditionBlocks
  );

  const ruleSummary = generateRuleSummary({
    triggerSources: triggerSources.map((m) => m.value),
    allSource: selectedRule.allSource,
    triggerGroup: selectedRule.triggerInfo.triggerGroup,
    triggerEvent: triggerActionLabel,
    occursEverytime: selectedRule.triggerInfo.occursEverytime,
    conditions: conditionSummaryItems,
    actions: actionItems,
  });

  const isLoading =
    isLoadingCalendars || isLoadingFolderData || isLoadingSharedDrives;
  if (isLoading)
    return (
      <Box
        sx={{
          px: 2,
          py: 4,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  return (
    <>
      <Box
        sx={{
          mt: 1.5,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 1,
          bgcolor: theme.palette.semantic.surface.info,
          color: theme.palette.colors.neutral[600],
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        <Typography variant="caption">{ruleSummary}</Typography>
      </Box>
      <RulesSummaryWhenSection
        triggerInfo={selectedRule.triggerInfo}
        triggerSources={triggerSources.map((s) => s.value)}
        allSource={selectedRule.allSource}
      />
      <RulesSummaryIfSection conditionBlocks={selectedRule.conditionBlocks} />
      <RuleSummaryThenSection actionTodo={actionTodo} />
    </>
  );
}
